import { FC, useState } from "react"
import { css } from "@emotion/react"
import { useHistory, useRouteMatch } from "react-router-dom"
import { initialCustomerPartQueryParams, usePartCategories, usePartChemicals } from "@ncs/ncs-api"
import {
	AnimatedEntrance,
	encodeUrlState,
	Icon,
	TrackingEvent,
	useUrlState,
	Flag,
	useIsFeatureFlagOn,
} from "@ncs/web-legos"
import { ShopBarNavSubmenuEcomm } from "./ShopBarNavSubmenuEcomm"
import { PartSearchResultsUrlState } from "~/views/shop/part-search-results"
import { useAnalytics } from "~/hooks/useAnalytics"

export type ShopBarNavUrlState = Pick<
	PartSearchResultsUrlState,
	"categories" | "manufacturers" | "systems" | "featured" | "clearance"
>

export const ShopBarNavEcomm: FC = () => {
	const history = useHistory()
	const [categories] = usePartCategories()
	const [chemicals] = usePartChemicals()
	// eslint-disable-next-line
	const [showCategoriesSubmenu, setShowCategoriesSubmenu] = useState(false)
	const [showChemicalsSubmenu, setShowChemicalsSubmenu] = useState(false)
	const { trackEvent } = useAnalytics()
	const hideTrending = useIsFeatureFlagOn(Flag.VITE_FLAG_HIDE_TRENDING)

	const [, { setUrlState }] = useUrlState<ShopBarNavUrlState>({
		categories: initialCustomerPartQueryParams.categories,
		manufacturers: initialCustomerPartQueryParams.manufacturers,
		systems: initialCustomerPartQueryParams.systems,
		featured: null,
		clearance: null,
	})
	const isOnSearchPage = useRouteMatch("/shop/search")

	const viewResult = (urlState: Partial<PartSearchResultsUrlState>) => {
		if (isOnSearchPage) {
			setUrlState({
				...initialCustomerPartQueryParams,
				...urlState,
			})
		} else {
			history.push({
				pathname: "/shop/search",
				search: encodeUrlState<PartSearchResultsUrlState>(urlState),
			})
		}
	}

	const onViewCategory = (categoryId: string) => {
		setShowCategoriesSubmenu(false)
		const urlState: Partial<PartSearchResultsUrlState> = {
			categories: [categoryId],
			page: 1,
		}
		viewResult(urlState)
	}

	const clearanceHandler = () => {
		viewResult({ clearance: true, page: 1 })
		trackEvent(TrackingEvent.CLEARANCE_NAV_BAR)
	}

	const trandingHandler = () => {
		viewResult({ featured: true, page: 1 })
		trackEvent(TrackingEvent.TRENDING)
	}

	const chemicalsSubmenuHandler = () => {
		setShowChemicalsSubmenu(true)
		trackEvent(TrackingEvent.CHEMISTRY_CATEGORY)
	}

	const partsSubmenuHandler = () => {
		setShowCategoriesSubmenu(true)
		trackEvent(TrackingEvent.PARTS_CATEGORY)
	}

	return (
		<ul css={navListStyle}>
			<li
				className="has-children"
				onMouseEnter={partsSubmenuHandler}
				onMouseLeave={() => setShowCategoriesSubmenu(false)}
			>
				<span>
					<span>Parts</span>
					<span style={{ marginLeft: "5px" }}>
						<Icon icon="chevron-down" />
					</span>
				</span>
				<AnimatedEntrance
					show={showCategoriesSubmenu}
					css={submenuContainerStyle}
					duration={400}
					delay={400}
				>
					<ShopBarNavSubmenuEcomm
						items={(categories ?? []).map((category) => ({
							label: category.name ?? category.id,
							onClickValue: category.id,
						}))}
						onClick={onViewCategory}
					/>
				</AnimatedEntrance>
			</li>
			<li
				className="has-children"
				onMouseEnter={chemicalsSubmenuHandler}
				onMouseLeave={() => setShowChemicalsSubmenu(false)}
			>
				<span>
					<span>Chemicals</span>
					<span style={{ marginLeft: "5px" }}>
						<Icon icon="chevron-down" />
					</span>
				</span>
				<AnimatedEntrance
					show={showChemicalsSubmenu}
					css={submenuContainerStyle}
					duration={400}
					delay={400}
				>
					<ShopBarNavSubmenuEcomm
						columnCount={2}
						items={(chemicals ?? []).map((chemical) => ({
							label: chemical.name ?? chemical.id,
							onClickValue: chemical.id,
						}))}
						onClick={onViewCategory}
					/>
				</AnimatedEntrance>
			</li>
			{!hideTrending && (
				<li>
					<button css={navLinkButton} onClick={trandingHandler}>
						<span>Trending</span>
					</button>
				</li>
			)}
			<li>
				<button css={navLinkButton} onClick={clearanceHandler}>
					<span>Clearance</span>
				</button>
			</li>
		</ul>
	)
}

const navListStyle = () => css`
	display: flex;
	align-items: center !important;
	justify-content: space-between !important;
	background-color: #f2f2f3;
	color: #00050a;
	font-size: 14px;
	font-style: normal;
	font-weight: 500 !important;
	line-height: normal;
	display: flex;
	column-gap: 0.5rem;
	padding: 1rem 0;
	z-index: 2;

	& * {
		color: #00050a !important;
	}

	li {
		font-size: 14px !important;
		line-height: normal;
		font-weight: 500 !important;
		font-style: normal;
		box-sizing: border-box;
		position: relative;
		margin-right: 1.5rem;
		padding-bottom: 2px;

		/* This is required to keep the hover state active */
		&.has-children::after {
			content: "";
			position: absolute;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			height: 4rem;
			z-index: 2;
		}

		&:hover,
		button:hover {
			cursor: pointer;
		}

		> span,
		a {
			color: white;
			background: none;
			padding: 0;
			border: 0;
			text-decoration: none;
			&:hover,
			&:focus {
				color: white;
			}
		}
		&.has-children {
			transition: border-color 300ms ease-out 100ms;
			> span {
				cursor: pointer;
			}
			&:hover {
				border-color: white;
			}
		}
	}
`

const submenuContainerStyle = css`
	position: absolute;
	z-index: 1;
	top: 2rem;
	left: -1rem;
	box-shadow: 0 0 4px rgba(0, 0, 0, 0.25);
	background: #ffffff;
	border-radius: 0.5rem;

	&:hover {
		box-shadow: 0 0 8px 0 #d1d5db;
	}
`

const navLinkButton = css`
	color: white;
	border: 0;
	padding: 0;
	background: none;
`
